import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageMenu } from './LanguageMenu'
import { useTheme } from '../app/useTheme'

interface AuthScreenProps {
  /** Undefined = no badge (a bare unauthenticated visit). Set to render the
   * prototype's `.login-signed-out` pill in the danger treatment — used for
   * both "You have been signed out" and "Access denied" (same class, see
   * `.login-card .login-signed-out` in the prototype). */
  badgeLabel?: string
  subtitle: string
  /** The prototype's `.login-error` box, under the subtitle: a failed
   * Cognito sign-in or account choice (MS-993). A fixed, translated string. */
  error?: string
  children?: ReactNode
}

/** The prototype's `.login-card .btn-primary` on top of `Button`'s primary
 * variant: full width, 10px padding, 14px, centred, 8px gap. Wraps rather
 * than truncates, so a long label fits the card in every locale. */
export const AUTH_PRIMARY_BUTTON_CLASS =
  'w-full justify-center text-center whitespace-normal !p-[10px] !text-[14px] !gap-2'

/**
 * The shared pre-authentication screen — matches the prototype's single
 * `#login-screen` element, which every one of its three states (bare visit,
 * signed out, access denied) reuses by toggling a badge and swapping the
 * subtitle, rather than rendering as separate pages. This app renders those
 * three states as separate components (Login.tsx, AccessDenied.tsx) per
 * this repo's own React/Tailwind rebuild convention (see
 * ARCHITECTURE_DECISIONS.md §7) — sharing this one component keeps their
 * rendered output identical instead of drifting into two implementations.
 */
export function AuthScreen({ badgeLabel, subtitle, error, children }: Readonly<AuthScreenProps>) {
  const { t } = useTranslation()
  const { isDark, toggle } = useTheme()

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-teal-900 dark:bg-[var(--bg-app)] px-4">
      <div className="fixed top-[18px] right-5 flex items-center gap-2.5">
        <button
          onClick={toggle}
          className="w-8 h-8 rounded-md border border-white/20 bg-white/10 text-white/80 hover:text-white hover:border-white/50 flex items-center justify-center transition-colors"
          title={t('appShell.toggleTheme')}
          aria-label={t('appShell.toggleTheme')}
        >
          {isDark ? (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
          )}
        </button>
        <LanguageMenu />
      </div>

      <img src="/assets/mambu-logo.png" alt="Mambu" className="w-12 h-12" />

      {/* Dark mode rings the card with an inset shadow, as the prototype
          does, not a border: a border would take 2px off the content box and
          re-wrap the copy when the theme switches. */}
      <div className="bg-[#f0f1f3] dark:bg-[var(--bg-card)] rounded-2xl px-9 pt-8 pb-9 w-[400px] max-w-[90vw] shadow-[0_20px_60px_rgba(0,0,0,0.3)] dark:shadow-[inset_0_0_0_1px_var(--border),0_20px_60px_rgba(0,0,0,0.55)] text-center">
        <h2 className="font-semibold text-[20px] text-[var(--text-primary)] mt-0 mb-0.5">
          {t('login.brand')}
        </h2>
        <div className="text-[11px] font-normal uppercase tracking-wide text-[var(--text-tertiary)] mb-1.5">
          {t('login.product')}
        </div>

        {badgeLabel && (
          <div className="inline-block bg-[var(--danger-bg)] text-[var(--danger)] text-[11px] font-semibold px-3 py-1 rounded-full mb-3">
            {badgeLabel}
          </div>
        )}

        <div className="text-[13px] text-[var(--text-secondary)] mb-5">{subtitle}</div>

        {error && (
          <div role="alert" className="bg-[var(--danger-bg)] text-[var(--danger)] text-[12px] px-[14px] py-[10px] rounded-lg mb-4">
            {error}
          </div>
        )}

        {children}
      </div>
    </div>
  )
}
